import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { CSAT_RATING_VALUES, CSAT_SURVEY_STATUSES, CSAT_SURVEY_STATUS_LABELS } from '@troia-v3/types';
import type {
  CsatRatingValue,
  CsatReportFilters,
  CsatReportItem,
  CsatSurveyStatus,
} from '@troia-v3/types';

import {
  Button,
  FilterBottomSheet,
  FilterChip,
  FiltersButton,
  ListPageEmpty,
  ListPageLoading,
  ListPagePagination,
  PageToolbar,
  PeriodPicker,
  PlainHeadCell,
  Select,
  StatusPill,
} from '@/shared/components/ui';
import type { PeriodPickerValue, StatusPillVariant } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { usePermissions } from '@/hooks/usePermissions';
import { useAgentsDropdown, type AgentDropdownItem } from '@/modules/automation/ai-agents/hooks/useAgentsDropdown';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';

import { CsatSummaryPanel } from '../components/CsatSummaryPanel';
import { useCsatReport, useCsatSummary } from '../hooks/useCsatReport';
import { CsatExportTruncatedError, csatReportApi } from '../services/csatReportApi';

const PAGE_SIZE = 20;

export const CSAT_STATUS_VARIANT: Record<CsatSurveyStatus, StatusPillVariant> = {
  answered: 'success',
  awaiting: 'info',
  no_answer: 'neutral',
  invalid: 'warning',
};

/** Janela rolling (dias) por modo do PeriodPicker — molde `ChecklistsPage`. */
const PERIOD_MODE_DAYS: Record<'week' | 'month' | 'year', number> = { week: 7, month: 30, year: 365 };

const fromYmd = (ymd: string): Date => {
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
};

const startOfLocalDay = (date: Date): string => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

const endOfLocalDay = (date: Date): string => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};

/**
 * Período do filtro em instantes — início e fim do dia NO FUSO do navegador.
 * Mandar só `YYYY-MM-DD` faria o servidor contar o dia em UTC e o atendimento
 * encerrado às 22h de ontem cairia em "hoje".
 */
export const periodToRange = (period: PeriodPickerValue, now = new Date()): { startDate: string; endDate: string } => {
  if (period.mode === 'custom' && period.start && period.end) {
    return { startDate: startOfLocalDay(fromYmd(period.start)), endDate: endOfLocalDay(fromYmd(period.end)) };
  }
  const days = period.mode === 'custom' ? PERIOD_MODE_DAYS.month : PERIOD_MODE_DAYS[period.mode];
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  return { startDate: startOfLocalDay(start), endDate: endOfLocalDay(now) };
};

const formatDayMonth = (ymd: string): string => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}`;

const periodLabelOf = (period: PeriodPickerValue): string => {
  if (period.mode === 'custom' && period.start && period.end) {
    return `${formatDayMonth(period.start)} - ${formatDayMonth(period.end)}`;
  }
  if (period.mode === 'week') return 'Última semana';
  if (period.mode === 'year') return 'Último ano';
  return 'Último mês';
};

const formatDateTime = (iso: string | null): string => (iso ? format(new Date(iso), 'dd/MM/yyyy HH:mm') : '—');

/** Filtros que o resumo usa — situação e nota ficam de fora (o servidor as ignora). */
const summaryFiltersOf = ({ status: _status, rating: _rating, ...rest }: CsatReportFilters): CsatReportFilters => rest;

type FilterKey = keyof CsatReportFilters;

/**
 * Relatórios → CSAT (2026-09-23). Uma linha por atendimento que recebeu a
 * pesquisa de satisfação — pelo encerramento com o CSAT do canal ou pelo nó
 * CSAT de workflow —, encerramento mais recente primeiro. Clicar abre a
 * conversa. Permissão própria `reports-csat` (read / export).
 */
export const CsatReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasModulePermission } = usePermissions();
  const canExport = hasModulePermission('reports-csat', 'export');

  const [filters, setFilters] = useState<CsatReportFilters>({});
  const [page, setPage] = useState(1);
  const [periodValue, setPeriodValue] = useState<PeriodPickerValue | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const query = useMemo(() => ({ ...filters, page, limit: PAGE_SIZE }), [filters, page]);
  const summaryFilters = useMemo(() => summaryFiltersOf(filters), [filters]);

  const { data, isLoading, error } = useCsatReport(query);
  const { data: summary } = useCsatSummary(summaryFilters);

  const { data: channels } = useChannelsDropdown();
  const { data: teams } = useTeamsDropdown();
  const { data: users } = useUsersDropdown();
  const { data: agents } = useAgentsDropdown();

  const channelOptions = useMemo(() => (channels ?? []).map((c) => ({ value: c.id, label: c.name })), [channels]);
  const teamOptions = useMemo(() => (teams ?? []).map((t) => ({ value: t.id, label: t.name })), [teams]);
  const userOptions = useMemo(() => (users ?? []).map((u) => ({ value: u.id, label: u.name })), [users]);
  // O hook devolve `any` (map sobre a resposta crua) — tipado aqui.
  const agentOptions = useMemo(
    () => ((agents ?? []) as AgentDropdownItem[]).map((a) => ({ value: a.id, label: a.name })),
    [agents],
  );

  const items = data?.items ?? [];

  const setFilter = <K extends FilterKey>(key: K, value: CsatReportFilters[K] | undefined) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === undefined || value === '') delete next[key];
      else next[key] = value;
      // Nota só existe em pesquisa respondida: outra situação apaga a nota.
      if (key === 'status' && value !== undefined && value !== 'answered') delete next.rating;
      return next;
    });
    setPage(1);
  };

  const applyPeriod = (value: PeriodPickerValue) => {
    setPeriodValue(value);
    const { startDate, endDate } = periodToRange(value);
    setFilters((prev) => ({ ...prev, startDate, endDate }));
    setPage(1);
  };

  const clearPeriod = () => {
    setPeriodValue(null);
    setFilters(({ startDate: _start, endDate: _end, ...rest }) => rest);
    setPage(1);
  };

  const clearAllFilters = () => {
    setPeriodValue(null);
    setFilters({});
    setPage(1);
    setFiltersOpen(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await csatReportApi.exportCsv(filters);
      toast.success('Planilha exportada');
    } catch (err) {
      if (err instanceof CsatExportTruncatedError) {
        toast.warning(err.message);
      } else {
        toast.error('Não foi possível exportar a planilha. Tente de novo.');
      }
    } finally {
      setExporting(false);
    }
  };

  const labelOf = (options: Array<{ value: string; label: string }>, id?: string): string | null =>
    id ? (options.find((o) => o.value === id)?.label ?? 'Selecionado') : null;

  const chips: Array<{ key: string; label: string; value: string; onRemove: () => void }> = [];
  if (filters.status) chips.push({ key: 'status', label: 'Situação', value: CSAT_SURVEY_STATUS_LABELS[filters.status], onRemove: () => setFilter('status', undefined) });
  if (filters.rating !== undefined) chips.push({ key: 'rating', label: 'Nota', value: String(filters.rating), onRemove: () => setFilter('rating', undefined) });
  const channelLabel = labelOf(channelOptions, filters.channelId);
  if (channelLabel) chips.push({ key: 'channel', label: 'Canal', value: channelLabel, onRemove: () => setFilter('channelId', undefined) });
  const teamLabel = labelOf(teamOptions, filters.teamId);
  if (teamLabel) chips.push({ key: 'team', label: 'Equipe', value: teamLabel, onRemove: () => setFilter('teamId', undefined) });
  const assigneeLabel = labelOf(userOptions, filters.assigneeId);
  if (assigneeLabel) chips.push({ key: 'assignee', label: 'Atendente', value: assigneeLabel, onRemove: () => setFilter('assigneeId', undefined) });
  const agentLabel = labelOf(agentOptions, filters.agentId);
  if (agentLabel) chips.push({ key: 'agent', label: 'Agente', value: agentLabel, onRemove: () => setFilter('agentId', undefined) });
  if (periodValue) chips.push({ key: 'period', label: 'Período', value: periodLabelOf(periodValue), onRemove: clearPeriod });

  const activeFilterCount = chips.length;
  const ratingEnabled = filters.status === undefined || filters.status === 'answered';

  const sectionTitle = (text: string) => (
    <p className="px-0.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>{text}</p>
  );

  const filterOptions = (
    <div className="space-y-3">
      <div>
        {sectionTitle('Situação')}
        <div className="space-y-0.5">
          {[undefined, ...CSAT_SURVEY_STATUSES].map((status) => {
            const active = filters.status === status;
            return (
              <button
                key={status ?? 'all'}
                onClick={() => setFilter('status', status)}
                className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                data-testid={`csat-filter-status-${status ?? 'all'}`}
              >
                {status ? CSAT_SURVEY_STATUS_LABELS[status] : 'Todas'}
                {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        {sectionTitle('Nota')}
        <Select
          value={filters.rating !== undefined ? String(filters.rating) : ''}
          onChange={(value) => setFilter('rating', value ? (Number(value) as CsatRatingValue) : undefined)}
          options={[
            { value: '', label: 'Todas as notas' },
            ...CSAT_RATING_VALUES.map((value) => ({
              value: String(value),
              label: value === 1 ? '1 (melhor)' : value === 5 ? '5 (pior)' : String(value),
            })),
          ]}
          disabled={!ratingEnabled}
          data-testid="csat-filter-rating"
        />
      </div>
      <div>
        {sectionTitle('Canal')}
        <Select
          value={filters.channelId ?? ''}
          onChange={(value) => setFilter('channelId', value)}
          options={[{ value: '', label: 'Todos os canais' }, ...channelOptions]}
          data-testid="csat-filter-channel"
        />
      </div>
      <div>
        {sectionTitle('Equipe')}
        <Select
          value={filters.teamId ?? ''}
          onChange={(value) => setFilter('teamId', value)}
          options={[{ value: '', label: 'Todas as equipes' }, ...teamOptions]}
          data-testid="csat-filter-team"
        />
      </div>
      <div>
        {sectionTitle('Atendente')}
        <Select
          value={filters.assigneeId ?? ''}
          onChange={(value) => setFilter('assigneeId', value)}
          options={[{ value: '', label: 'Todos os atendentes' }, ...userOptions]}
          data-testid="csat-filter-assignee"
        />
      </div>
      <div>
        {sectionTitle('Agente de IA')}
        <Select
          value={filters.agentId ?? ''}
          onChange={(value) => setFilter('agentId', value)}
          options={[{ value: '', label: 'Todos os agentes' }, ...agentOptions]}
          data-testid="csat-filter-agent"
        />
      </div>
      <div>
        <div className="flex items-center justify-between px-0.5 pb-1">
          <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Período do encerramento</p>
          {periodValue && (
            <button onClick={clearPeriod} className="text-xs font-semibold" style={{ color: '#8b5cf6' }} data-testid="csat-filter-period-clear">
              Limpar
            </button>
          )}
        </div>
        <PeriodPicker value={periodValue ?? { mode: 'month' }} onChange={applyPeriod} data-testid="csat-filter-period" />
      </div>
      {activeFilterCount > 0 && (
        <button
          onClick={clearAllFilters}
          className="w-full px-2.5 py-2 rounded-lg text-sm font-semibold"
          style={{ color: '#8b5cf6' }}
          data-testid="csat-filter-clear-all"
        >
          Limpar filtros
        </button>
      )}
    </div>
  );

  const exportButton = canExport ? (
    <Button
      variant="primary"
      icon="download"
      collapsibleLabel
      onClick={() => { void handleExport(); }}
      disabled={exporting}
      data-testid="csat-export"
    >
      {exporting ? 'Exportando…' : 'Exportar planilha'}
    </Button>
  ) : null;

  const openConversation = (item: CsatReportItem) => navigate(`/chat/${item.conversationId}`);

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      <PageToolbar
        left={
          <>
            <nav className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>CSAT</span>
            </nav>
            {data?.total != null && (
              <span
                className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
                style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
                data-testid="csat-count"
              >
                {data.total}
              </span>
            )}
          </>
        }
        right={
          <>
            <div className="relative flex-none">
              <FiltersButton
                count={activeFilterCount}
                active={filtersOpen}
                onClick={() => setFiltersOpen((v) => !v)}
                data-testid="csat-filters"
              />
              {filtersOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                  <div
                    className="absolute right-0 top-full mt-2 z-50 w-72 rounded-xl p-2 max-h-[75vh] overflow-y-auto"
                    style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                    data-testid="csat-filters-popover"
                  >
                    {filterOptions}
                  </div>
                </>
              )}
            </div>
            {exportButton && (
              <>
                <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />
                {exportButton}
              </>
            )}
          </>
        }
        mobile={
          <>
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="csat-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
            <span className="flex-1" />
            {exportButton}
          </>
        }
        below={
          activeFilterCount > 0 ? (
            <div
              className="flex-none flex items-center flex-wrap gap-2 px-4 py-2"
              style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}
              data-testid="csat-filterbar"
            >
              {chips.map((chip) => (
                <FilterChip
                  key={chip.key}
                  label={chip.label}
                  value={chip.value}
                  onClick={() => setFiltersOpen(true)}
                  onRemove={chip.onRemove}
                  data-testid={`csat-chip-${chip.key}`}
                />
              ))}
              <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="csat-clear-filters">
                Limpar tudo
              </button>
            </div>
          ) : undefined
        }
      />

      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 space-y-4">
          {summary && <CsatSummaryPanel summary={summary} />}

          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }} data-testid="csat-error">
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar o relatório: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando notas..." />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="star"
              message="Nenhuma pesquisa encontrada"
              description={
                activeFilterCount > 0
                  ? 'Tente ajustar os filtros.'
                  : 'As notas aparecem aqui quando a pesquisa de satisfação é enviada no encerramento do atendimento — pelo CSAT do canal ou pelo nó CSAT dos workflows.'
              }
            />
          )}

          {!isLoading && !error && items.length > 0 && (
            <>
              <div className="grid grid-cols-1 gap-3 md:hidden" data-testid="csat-cards">
                {items.map((item) => (
                  <CsatCard key={item.conversationId} item={item} onClick={() => openConversation(item)} />
                ))}
              </div>
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse" data-testid="csat-table">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <PlainHeadCell label="Encerrada em" />
                      <PlainHeadCell label="Contato" />
                      <PlainHeadCell label="Nota" />
                      <PlainHeadCell label="Situação" />
                      <PlainHeadCell label="Canal" />
                      <PlainHeadCell label="Atendente" />
                      <PlainHeadCell label="Equipe" />
                      <PlainHeadCell label="Agente de IA" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <CsatRow key={item.conversationId} item={item} onClick={() => openConversation(item)} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!isLoading && !error && data && data.total > 0 && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={setPage}
              totalItems={data.total}
              pageSize={data.limit}
              itemLabel="atendimentos"
            />
          )}
        </div>
      </div>

      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="csat-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>
    </div>
  );
};

CsatReportPage.displayName = 'CsatReportPage';

/* ------------------------------------------------------------------ */
/* Linha de tabela / card                                              */
/* ------------------------------------------------------------------ */

interface CsatItemProps {
  item: CsatReportItem;
  onClick: () => void;
}

const RatingValue: React.FC<{ item: CsatReportItem }> = ({ item }) => {
  if (item.rating === null) return <span style={{ color: 'var(--theme-muted-text)' }}>—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={`csat-rating-${item.conversationId}`}>
      {item.ratingEmoji && <span aria-hidden>{item.ratingEmoji}</span>}
      <span className="font-semibold tabular-nums">{item.rating}</span>
      {item.ratingLabel && <span style={{ color: 'var(--theme-muted-text)' }}>{item.ratingLabel}</span>}
    </span>
  );
};

const CsatRow: React.FC<CsatItemProps> = ({ item, onClick }) => (
  <tr
    className="cursor-pointer transition-colors border-t"
    style={{ borderColor: 'var(--theme-card-border)' }}
    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)')}
    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
    onClick={onClick}
    data-testid={`csat-row-${item.conversationId}`}
  >
    <td className="px-4 py-3 text-sm tabular-nums whitespace-nowrap" style={{ color: 'var(--theme-card-text)' }}>
      {formatDateTime(item.endedAt)}
    </td>
    <td className="px-4 py-3 text-sm max-w-[240px]" style={{ color: 'var(--theme-card-text)' }}>
      <div className="font-semibold truncate">{item.contact?.name ?? item.contact?.phone ?? '—'}</div>
      {item.contact?.name && item.contact.phone && (
        <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>{item.contact.phone}</div>
      )}
    </td>
    <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-card-text)' }}>
      <RatingValue item={item} />
    </td>
    <td className="px-4 py-3">
      <StatusPill variant={CSAT_STATUS_VARIANT[item.status]} label={CSAT_SURVEY_STATUS_LABELS[item.status]} data-testid={`csat-status-${item.conversationId}`} />
    </td>
    <td className="px-4 py-3 text-sm truncate max-w-[180px]" style={{ color: 'var(--theme-card-text)' }}>{item.channel?.name ?? '—'}</td>
    <td className="px-4 py-3 text-sm truncate max-w-[180px]" style={{ color: 'var(--theme-card-text)' }}>{item.assignee?.name ?? '—'}</td>
    <td className="px-4 py-3 text-sm truncate max-w-[160px]" style={{ color: 'var(--theme-card-text)' }}>{item.team?.name ?? '—'}</td>
    <td className="px-4 py-3 text-sm truncate max-w-[160px]" style={{ color: 'var(--theme-card-text)' }}>{item.agent?.name ?? '—'}</td>
  </tr>
);

const CsatCard: React.FC<CsatItemProps> = ({ item, onClick }) => {
  const who = [item.assignee?.name, item.team?.name, item.agent?.name].filter(Boolean).join(' · ');
  return (
    <div
      className="rounded-xl p-4 cursor-pointer transition-colors"
      style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
      onClick={onClick}
      data-testid={`csat-card-${item.conversationId}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>
          {item.contact?.name ?? item.contact?.phone ?? 'Contato'}
        </h3>
        <StatusPill variant={CSAT_STATUS_VARIANT[item.status]} label={CSAT_SURVEY_STATUS_LABELS[item.status]} />
      </div>
      <div className="text-sm mb-1" style={{ color: 'var(--theme-card-text)' }}>
        <RatingValue item={item} />
      </div>
      <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
        {formatDateTime(item.endedAt)}{item.channel ? ` · ${item.channel.name}` : ''}
      </p>
      {who && (
        <p className="text-xs truncate mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>{who}</p>
      )}
    </div>
  );
};
